import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { IconContext } from "react-icons";
import {
    FiLayout,
    FiBriefcase,
    FiCode,
    FiSettings,
    FiSearch,
    FiLogOut,
    FiChevronDown,
    FiMenu,
    FiMessageSquare,
    FiPlus,
    FiTrash2,
    FiCreditCard
} from "react-icons/fi";
import Chad from "./images/chad.png";
import "./DashboardSidebar.css";

export interface ChatSessionListItem {
    id: string;
    title: string;
    updatedAt: string;
    /** Language from plan (e.g. Python, JavaScript) */
    language?: string;
    /** When the session was first created */
    createdAt?: string;
    /** Research session: agent task ID */
    taskId?: string;
    /** Session type: chat (default) or research */
    type?: "chat" | "research";
}

export interface DashboardSidebarProps {
    darkMode: boolean;
    accountType: "company" | "job_seeker";
    companyName?: string;
    userName?: string;
    profilePhotoUrl?: string | null;
    onLogout: () => void;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    /** Chat sessions (company only) */
    chatSessions?: ChatSessionListItem[];
    activeChatSessionId?: string | null;
    onSessionSelect?: (sessionId: string) => void;
    onSessionDelete?: (sessionId: string) => void;
    onNewSession?: () => void;
}

function formatSessionDate(isoString: string): string {
    const d = new Date(isoString);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    if (isToday) {
        return `Today, ${time}`;
    }
    const isThisYear = d.getFullYear() === now.getFullYear();
    const date = d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        ...(isThisYear ? {} : { year: "numeric" }),
    });
    return `${date}, ${time}`;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
    darkMode,
    accountType,
    companyName,
    userName,
    profilePhotoUrl,
    onLogout,
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "Search...",
    chatSessions = [],
    activeChatSessionId = null,
    onSessionSelect,
    onSessionDelete,
    onNewSession,
}) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search || "");
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        return localStorage.getItem('sidebarCollapsed') === 'true';
    });

    useEffect(() => {
        if (isCollapsed) {
            document.body.classList.add('sidebar-collapsed');
        } else {
            document.body.classList.remove('sidebar-collapsed');
        }
        localStorage.setItem('sidebarCollapsed', String(isCollapsed));
    }, [isCollapsed]);

    const isSettingsActive = location.pathname === "/dashboard/settings";
    const isCreditsActive = location.pathname === "/dashboard/credits";
    const view = searchParams.get("view");

    const isOverviewActive = () => {
        if (accountType === "company") {
            return location.pathname === "/dashboard" && (view === "overview" || view === null || view === "");
        }
        return location.pathname === "/dashboard";
    };
    const isInterviewsActive = () => {
        if (accountType === "company") {
            return (
                (location.pathname === "/dashboard" && view === "interviews") ||
                location.pathname.startsWith("/company/interview") ||
                location.pathname.startsWith("/company/results")
            );
        }
        return location.pathname.startsWith("/interviews");
    };

    const navItems =
        accountType === "company"
            ? [
                { to: "/dashboard", label: "Overview", icon: FiLayout, active: isOverviewActive() },
                { to: "/dashboard?view=interviews", label: "Interviews", icon: FiBriefcase, active: isInterviewsActive() },
                { to: "/dashboard/credits", label: "Credits", icon: FiCreditCard, active: isCreditsActive },
                { to: "/dashboard/settings", label: "Settings", icon: FiSettings, active: isSettingsActive },
            ]
            : [
                { to: "/dashboard", label: "Sandboxes", icon: FiCode, active: location.pathname === "/dashboard" },
                { to: "/interviews", label: "Interviews", icon: FiBriefcase, active: location.pathname.startsWith("/interviews") },
                { to: "/dashboard/settings", label: "Settings", icon: FiSettings, active: isSettingsActive },
            ];

    return (
        <aside
            className={`dashboard-sidebar ${darkMode ? "dashboard-sidebar-dark" : ""} ${isCollapsed ? "collapsed" : ""}`}
        >
            <div className="sidebar-content">
                {/* Top: Project/Team selector */}
                <div className="sidebar-top">
                    <div className="sidebar-toggle-container">
                        <button
                            className="sidebar-toggle-btn"
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        >
                            <FiMenu />
                        </button>
                    </div>
                    {onSearchChange && (
                        <div className="sidebar-search">
                            <FiSearch className="sidebar-search-icon" />
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchValue}
                                onChange={(e) => onSearchChange(e.target.value)}
                                className="sidebar-search-input"
                            />
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="sidebar-nav">
                    {navItems.map((item) => {
                        const Icon = item.icon;

                        return (
                            <Link
                                key={item.label}
                                to={item.to}
                                className={`sidebar-nav-item ${item.active ? "active" : ""}`}
                            >
                                <IconContext.Provider
                                    value={{
                                        size: "1.1em",
                                        style: { flexShrink: 0 },
                                    }}
                                >
                                    <Icon />
                                </IconContext.Provider>
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </nav>

                {/* Sessions - show when on dashboard (chat panel visible) */}
                {location.pathname === "/dashboard" && (onSessionSelect || onNewSession) && (
                    <div className="sidebar-sessions">
                        <div className="sidebar-sessions-header">
                            <FiMessageSquare className="sidebar-sessions-icon" />
                            <span>Sessions</span>
                            {onNewSession && (
                                <button
                                    className="sidebar-new-session-btn"
                                    onClick={onNewSession}
                                    title="New chat"
                                >
                                    <FiPlus />
                                </button>
                            )}
                        </div>
                        <div className="sidebar-sessions-list">
                            {chatSessions.length === 0 ? (
                                <div className="sidebar-sessions-empty">
                                    No past chats yet
                                </div>
                            ) : (
                                chatSessions.map((session) => (
                                    <div
                                        key={session.id}
                                        className={`sidebar-session-item ${activeChatSessionId === session.id ? "active" : ""}`}
                                    >
                                        <button
                                            className="sidebar-session-item-btn"
                                            onClick={() => onSessionSelect?.(session.id)}
                                            title={session.title}
                                        >
                                            <span className="sidebar-session-title">{session.title}</span>
                                            <div className="sidebar-session-meta">
                                                {session.language && (
                                                    <span className="sidebar-session-tag">{session.language}</span>
                                                )}
                                                <span className="sidebar-session-date">
                                                    {session.createdAt || session.updatedAt
                                                        ? formatSessionDate(session.createdAt || session.updatedAt)
                                                        : ""}
                                                </span>
                                            </div>
                                        </button>
                                        {onSessionDelete && (
                                            <button
                                                className="sidebar-session-delete"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSessionDelete(session.id);
                                                }}
                                                title="Delete chat"
                                            >
                                                <FiTrash2 size={12} />
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                <div className="sidebar-spacer" />

                {/* Bottom: User/Account */}
                <div className="sidebar-bottom">
                    <div className="sidebar-user">
                        <div className="sidebar-user-avatar">
                            {profilePhotoUrl ? (
                                <img src={profilePhotoUrl} alt="" />
                            ) : (
                                (companyName || userName || "U").charAt(0).toUpperCase()
                            )}
                        </div>
                        <div className="sidebar-user-info">
                            <span className="sidebar-user-name">
                                {accountType === "company"
                                    ? companyName || "Company"
                                    : userName || "User"}
                            </span>
                            <span className="sidebar-user-role">
                                {accountType === "company" ? "Team" : "Account"}
                            </span>
                        </div>
                    </div>
                    <button
                        className="sidebar-logout"
                        onClick={onLogout}
                        title="Log out"
                    >
                        <FiLogOut size="1.2em" style={{ flexShrink: 0 }} />
                        <span>Log out</span>
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default DashboardSidebar;
